import { useTranslation } from "react-i18next";
import { Badge, Button } from "@akuna/ui";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CommissionModel } from "@/lib/commissions/types";

type Props = {
  models: CommissionModel[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreate?: () => void;
};

export function CommissionModelList({ models, selectedId, onSelect, onCreate }: Props) {
  const { t } = useTranslation("commissions");
  return (
    <aside aria-labelledby="commission-model-list" className="flex min-w-0 flex-[1_1_240px] flex-col gap-2.5 lg:max-w-xs">
      <div className="flex items-center justify-between gap-2">
        <h2 id="commission-model-list" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t("catalog.models.listTitle")}
        </h2>
        {onCreate && (
          <Button variant="ghost" size="sm" onClick={onCreate} className="text-primary">
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t("catalog.models.new")}
          </Button>
        )}
      </div>
      <ul className="flex flex-col gap-2">
        {models.map((m) => {
          const selected = m.id === selectedId;
          return (
            <li key={m.id}>
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => onSelect(m.id)}
                className={cn(
                  "flex w-full flex-col items-start gap-1 rounded-card border bg-card px-4 py-3.5 text-left transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  selected ? "border-primary shadow-sm ring-1 ring-primary" : "border-border hover:bg-muted",
                )}
              >
                <span className="flex w-full items-center gap-2">
                  <span className="truncate text-[15px] font-semibold">{m.name}</span>
                  {!m.is_active && <Badge>{t("catalog.models.inactive")}</Badge>}
                </span>
                <span className="text-sm text-muted-foreground">
                  {m.used_by === 0 ? t("catalog.models.usedByNone") : t("catalog.models.usedBy", { count: m.used_by })}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
